/**
 * DrawingEditorDialog - Shared component for editing drawing properties
 * Extracted from ConceptCanvas and PlayCanvas to eliminate code duplication
 */

import React from 'react'
import { DrawingPropertiesDialog } from '../toolbar/dialogs/DrawingPropertiesDialog'
import { playerHasPreSnapMovement as checkPlayerHasPreSnapMovement } from '@/utils/PresnapValidation.utils'
import type { Drawing } from '@/types'
import type { FieldCoordinateSystem } from '@/utils'
import type { PreSnapMovement } from './BaseCanvas.types'

interface DrawingEditorDialogProps {
	editingDrawing: {
		drawing: Drawing
		position: { x: number; y: number }
	} | null
	drawings: Drawing[]
	coordSystem: FieldCoordinateSystem
	onDrawingUpdate: (id: string, updates: Partial<Drawing>) => void
	onPreSnapUpdate: (drawingId: string, preSnap: PreSnapMovement | null) => void
	onClose: () => void
}

/**
 * Renders the DrawingPropertiesDialog when a drawing is being edited
 * Handles fetching fresh drawing state and computing pre-snap movement status
 */
export function DrawingEditorDialog({
										editingDrawing,
										drawings,
										coordSystem,
										onDrawingUpdate,
										onPreSnapUpdate,
										onClose,
									}: DrawingEditorDialogProps) {
	if (!editingDrawing) {
		return null
	}

	// Get fresh drawing from current state, not stale editingDrawing
	const freshDrawing = drawings.find(d => d.id === editingDrawing.drawing.id)
		|| editingDrawing.drawing

	return (
		<DrawingPropertiesDialog
			drawing={freshDrawing}
			position={editingDrawing.position}
			onUpdate={(updates) => {
				onDrawingUpdate(freshDrawing.id, {
					style: { ...freshDrawing.style, ...updates },
				})
			}}
			onClose={onClose}
			coordSystem={coordSystem}
			onAddPreSnapMovement={() => {
				const hasPreSnap = !!freshDrawing.preSnapMotion
				onPreSnapUpdate(
					freshDrawing.id,
					hasPreSnap ? null : { type: 'motion' },
				)
			}}
			playerHasPreSnapMovement={
				freshDrawing.playerId
					? checkPlayerHasPreSnapMovement(
						freshDrawing.playerId,
						drawings,
						freshDrawing.id,
					)
					: false
			}
		/>
	)
}
